/**
 * AvatarFactory — builds a render proxy from portable avatar identity data.
 *
 * Deterministic: the same `AvatarAppearance` produces the same mesh in every
 * world, which is exactly what makes you the Recognizable Stranger. A world may
 * later override *rendering* (swap materials/post), but the body topology and
 * proportions come only from the standardized body template + skeleton — never
 * from the world. Origin is at the feet (y = 0).
 *
 * P1 keeps this primitive-based (no GLB/VRM yet): material slots drive tints;
 * `textureRef`/`shaderHint` are cost-bearing identity refs that don't yet load
 * real assets (the asset pipeline arrives later) but do bump complexity.
 */

import * as THREE from 'three/webgpu';
import type { AvatarAppearance, MaterialSlots } from '@shared/avatar.js';

const FALLBACK_TINT = '#888888';

function slotColor(slots: MaterialSlots, name: string, fallback = FALLBACK_TINT): THREE.Color {
  const tint = slots[name]?.tint ?? fallback;
  return new THREE.Color(tint);
}

/** Build a standalone material for a slot (shaderHint nudges metalness/emissive). */
function slotMaterial(slots: MaterialSlots, name: string, fallback?: string): THREE.MeshStandardMaterial {
  const slot = slots[name];
  const mat = new THREE.MeshStandardMaterial({
    color: slotColor(slots, name, fallback ?? FALLBACK_TINT),
    roughness: 0.7,
    metalness: 0.05,
  });
  // A shader hint is a (cost-bearing) request for a fancier look; approximate it.
  if (slot?.shaderHint === 'metallic') {
    mat.metalness = 0.85;
    mat.roughness = 0.3;
  } else if (slot?.shaderHint === 'emissive') {
    mat.emissive = slotColor(slots, name, fallback ?? FALLBACK_TINT);
    mat.emissiveIntensity = 0.4;
  }
  return mat;
}

/** Track all geometries/materials created so the caller can dispose cleanly. */
export interface AvatarProxy {
  object: THREE.Object3D;
  dispose(): void;
}

export function buildAvatar(appearance: AvatarAppearance): AvatarProxy {
  const group = new THREE.Group();
  const disposables: { dispose(): void }[] = [];

  const track = <T extends THREE.BufferGeometry | THREE.Material>(x: T): T => {
    disposables.push(x);
    return x;
  };

  const skin = track(slotMaterial(appearance.materialSlots, 'skin', '#c8a07a'));
  const primary = track(slotMaterial(appearance.materialSlots, 'primary', '#3b6ea5'));
  const secondary = track(slotMaterial(appearance.materialSlots, 'secondary', '#2a2f38'));
  const accent = track(slotMaterial(appearance.materialSlots, 'accent', '#d08a40'));

  const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number): THREE.Mesh => {
    const m = new THREE.Mesh(track(geo), mat);
    m.position.set(x, y, z);
    group.add(m);
    return m;
  };

  switch (appearance.bodyTemplate) {
    case 'abstract': {
      // A floating cluster — simplest body, lowest base cost. No legs.
      add(new THREE.IcosahedronGeometry(0.45, 1), primary, 0, 1.1, 0);
      const ring = add(new THREE.TorusGeometry(0.55, 0.06, 8, 24), secondary, 0, 1.1, 0);
      ring.rotation.x = Math.PI / 2;
      add(new THREE.SphereGeometry(0.18, 16, 12), skin, 0, 1.7, 0);
      break;
    }
    case 'anthro': {
      buildHumanoidBody(add, skin, primary, secondary);
      // Ears + tail in the accent slot.
      const earL = add(new THREE.ConeGeometry(0.08, 0.22, 8), accent, -0.12, 1.78, 0);
      const earR = add(new THREE.ConeGeometry(0.08, 0.22, 8), accent, 0.12, 1.78, 0);
      earL.rotation.z = 0.3;
      earR.rotation.z = -0.3;
      const tail = add(new THREE.CapsuleGeometry(0.05, 0.5, 4, 8), accent, 0, 0.7, -0.28);
      tail.rotation.x = -0.5;
      break;
    }
    case 'humanoid':
    default:
      buildHumanoidBody(add, skin, primary, secondary);
      break;
  }

  return {
    object: group,
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
}

/** Shared humanoid silhouette used by humanoid + anthro templates. */
function buildHumanoidBody(
  add: (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number) => THREE.Mesh,
  skin: THREE.Material,
  primary: THREE.Material,
  secondary: THREE.Material,
): void {
  // Head.
  add(new THREE.SphereGeometry(0.16, 20, 16), skin, 0, 1.62, 0);
  // Torso (primary = shirt).
  add(new THREE.CapsuleGeometry(0.18, 0.42, 6, 12), primary, 0, 1.18, 0);
  // Hips + legs (secondary = pants).
  add(new THREE.BoxGeometry(0.34, 0.18, 0.22), secondary, 0, 0.86, 0);
  add(new THREE.CapsuleGeometry(0.08, 0.6, 4, 8), secondary, -0.1, 0.45, 0);
  add(new THREE.CapsuleGeometry(0.08, 0.6, 4, 8), secondary, 0.1, 0.45, 0);
  // Arms (skin).
  add(new THREE.CapsuleGeometry(0.06, 0.5, 4, 8), skin, -0.27, 1.12, 0);
  add(new THREE.CapsuleGeometry(0.06, 0.5, 4, 8), skin, 0.27, 1.12, 0);
}
